import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { valid, lt } from 'semver';
import moment from 'moment';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Table,
  Button,
} from 'reactstrap';

import { useAlertPlus } from 'hooks';
import config from 'config';
import { useUser } from 'context';

import AlertModal from 'components/AlertModal';
import Pagination from 'components/Pagination';
import DatePicker from 'components/DatePicker';
import CollapsibleCard from 'components/CollapsibleCard';

import {
  getAllAutorizacionesIOMA,
  getAllResponse,
  getIOMAIsBlocked,
} from 'features/autorizacionesioma/autorizacionesiomaAPI';
import { AutorizacionIOMA } from 'types/AutorizacionesIOMA';

interface Options {
  page: number;
  estado: string;
  fechaDesde: string;
  fechaHasta: string;
}

function List() {
  const user = useUser();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [items, setItems] = useState<AutorizacionIOMA[]>([]);

  const [alertShow, alertMessage, alertCallback, alert] = useAlertPlus('');

  const [canCreate, setCanCreate] = useState(false);

  const location = useLocation<{ prevOptions?: Options }>();

  // Opciones de paginacion y filtros
  const [currentOptions, setCurrentOptions] = useState<Options>(
    location.state?.prevOptions ?? {
      page: 1,
      estado: 'pendientes',
      fechaDesde: '',
      fechaHasta: '',
    },
  );

  // Carga inicial de datos
  useEffect(() => {
    fetch(`${ config.baseUrl }/system/version`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(res => res.json())
      .then(res => {
        if (!res || !res.au) {
          return;
        }

        const currentVersion = valid(res.au);
        if (!currentVersion) {
          return;
        }

        const reload = lt(config.version, currentVersion);

        if (reload) {
          window.location.reload();
        }
      })
      .catch(console.error);

    fetchData();
  }, []);

  useEffect(() => {
    setCanCreate(user?.data?.fact_ioma ?? false);
  }, [user]);

  const fetchData = async (newOptions: Partial<Options> = {}) => {
    try {
      // Merge de las nuevas opciones con las viejas
      const options = Object.assign({}, currentOptions, newOptions);

      setIsLoading(true);

      // Hacemos la petición
      await getAllAutorizacionesIOMA(matriculateId, options)
        .then((response: getAllResponse) => {
          const { rows, count } = response as { rows: AutorizacionIOMA[], count: number };
          setItems(rows);
          setTotal(count);
        });

      // Guardamos las opciones
      setCurrentOptions(options);
    } catch (err) {
      setItems([]);
      setTotal(0);
      alert('Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewAuthorization = async () => {
    await getIOMAIsBlocked()
      .then(res => {
        if (!res || res.status != 'ok') {
          alert(
            res?.payload?.message ??
            'El sitio se encuentra en mantenimiento por actualización, en unos minutos estará disponible nuevamente.',
          );
          return;
        }

        window.location.href = '/solicitudes/ioma/create';
      })
      .catch(console.error);
  };

  const changePage = (page: number) => {
    fetchData({
      page,
    });
  };

  const changeFilter = (key: string, value: unknown) => {
    const page = key != 'page' ? 1 : currentOptions.page;

    fetchData({
      page,
      [key]: value,
    });
  };

  return (
    <div className="content">
      <AlertModal isOpen={ alertShow } message={ alertMessage } onClose={ alertCallback } />
      <Row>
        <Col md={12}>
          <CollapsibleCard title="Filtros">
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="estado">
                    Estado
                  </Label>
                  <Input
                    id="estado"
                    name="estado"
                    type="select"
                    className="custom-select form-control"
                    value={currentOptions.estado ?? ''}
                    onChange={e => changeFilter('estado', e.target.value)}
                    disabled={isLoading}
                    required
                  >
                    <option value={'pendientes'}>Pendientes</option>
                    <option value={'autorizadas'}>Autorizadas</option>
                    <option value={'observadas'}>Observadas</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="fechaDesde">
                    Fecha Desde
                  </Label>
                  <DatePicker
                    id="fechaDesde"
                    name="fechaDesde"
                    selected={
                      currentOptions.fechaDesde
                        ? moment(currentOptions.fechaDesde).toDate()
                        : ''
                    }
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    className="date-picker form-control"
                    disabled={isLoading}
                    onChange={date => changeFilter('fechaDesde', date)}
                    autoComplete="off"
                    showYearDropdown
                    showMonthDropdown
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="fechaHasta">
                    Fecha Hasta
                  </Label>
                  <DatePicker
                    id="fechaHasta"
                    name="fechaHasta"
                    selected={
                      currentOptions.fechaHasta
                        ? moment(currentOptions.fechaHasta).toDate()
                        : ''
                    }
                    dateFormat="dd/MM/yyyy"
                    locale="es"
                    className="date-picker form-control"
                    disabled={isLoading}
                    onChange={date => changeFilter('fechaHasta', date)}
                    autoComplete="off"
                    showYearDropdown
                    showMonthDropdown
                  />
                </FormGroup>
              </Col>
            </Row>
          </CollapsibleCard>
          <Card>
            <CardHeader>
              { !!canCreate
                ?
                  <Row>
                    <Col md={8}></Col>
                    <Col md={4} className="text-right">
                      <Button className="btn-round" color="primary" onClick={handleNewAuthorization}>
                        Nueva Autorización
                      </Button>
                    </Col>
                  </Row>
                : null
              }
            </CardHeader>
            <CardBody>
              <Table>
                <thead>
                  <tr>
                    <th>Nombre Afiliado</th>
                    <th>Diagnóstico</th>
                    { currentOptions.estado === 'observadas'
                      ? <th>Resultado</th>
                      : <th>Módulo</th>
                    }
                    { currentOptions.estado === 'autorizadas'
                      ? <th>Fecha Aut.</th>
                      : <th>Fecha Orden</th>
                    }
                    <th>Sesiones</th>
                    <th>Bono 1</th>
                    { currentOptions.estado === 'autorizadas'
                      ? <th>Nro. Aut.</th>
                      : <th>Bono 2</th>
                    }
                    <th>Discapacidad</th>
                    { ['autorizadas', 'observadas'].includes(currentOptions.estado) && !!canCreate
                      ? <th>Acciones</th>
                      : <>
                          { ['autorizadas'].includes(currentOptions.estado)
                            ? <th>Autorización</th>
                            : null
                          }
                        </>
                    }
                  </tr>
                </thead>
                <tbody>
                  {
                    items.map((row, key) => (
                      <tr key={ `row_${ row.idAutorizacion }_${key}` }>
                        <td>{ row.apellidoNombre ?? '' }</td>
                        <td>{ row.diagnostico ?? '' }</td>
                        <td>
                          { currentOptions.estado === 'observadas'
                            ? row.resultado
                            : row.modulo
                          }
                        </td>
                        <td>
                          { ['autorizadas'].includes(currentOptions.estado)
                            ? moment(row.fechaAuto, 'YYYY-MM-DD').format('DD-MM-YYYY') ?? ''
                            : moment(row.fechaOrden, 'YYYY-MM-DD').format('DD-MM-YYYY') ?? ''
                          }
                        </td>
                        <td>{ row.sesiones ?? '' }</td>
                        <td>{ row.bono1 ?? '' }</td>
                        <td>
                          { ['autorizadas'].includes(currentOptions.estado)
                            ? row.nroAuto ?? ''
                            : row.bono2 ?? ''
                          }
                        </td>
                        <td>{ !!row.discapacidad ? 'Sí' : 'No' }</td>
                        { ['autorizadas'].includes(currentOptions.estado) && !!canCreate
                          ?
                            <td>
                              <Link
                                to={{
                                  pathname: `/solicitudes/ioma/${currentOptions.estado}/${ row.idAutorizacion }/edit`,
                                  state: { prevOptions: currentOptions },
                                }}
                                className="btn btn-round btn-primary btn-sm"
                                title='Ver'
                              >
                                <i className="fa fa-eye"></i>
                              </Link>
                            </td>
                          : <>
                              { ['autorizadas'].includes(currentOptions.estado)
                                ? <td>{ row.nroAuto ?? '' }</td>
                                : null
                              }
                            </>
                        }
                        { ['observadas'].includes(currentOptions.estado) && !!canCreate
                          ?
                            <td>
                              <Link
                                to={{
                                  pathname: `/solicitudes/ioma/${currentOptions.estado}/${ row.idAutorizacion }/edit`,
                                  state: { prevOptions: currentOptions },
                                }}
                                className="btn btn-round btn-primary btn-sm"
                                title='Editar'
                              >
                                <i className="fa fa-edit"></i>
                              </Link>
                            </td>
                          : null
                        }
                      </tr>
                    ))
                  }
                </tbody>
              </Table>
            </CardBody>
            <CardFooter className="py-4">
              <Row>
                <Col sm={6}></Col>
                <Col
                  sm={6}
                  className="d-flex justify-content-center d-sm-block"
                >
                  <Pagination
                    page={currentOptions.page}
                    count={total}
                    onChangePage={changePage}
                  />
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default List;
